import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Calendar, Users, Eye, Clock, Phone, Mail, MessageSquare, 
  ChevronDown, ChevronUp, RefreshCw, Home, Filter,
  CheckCircle, XCircle, AlertCircle, Lock, LogOut, Save, Edit2
} from 'lucide-react';
import { siteConfig } from '@/config/site';
import { VixenManager } from '@/components/admin/MediaManagers';
import LibraryPanel from '@/components/admin/LibraryPanel';
import CommunicationsPanel from '@/components/admin/CommunicationsPanel';
import SchedulePanel from '@/components/admin/SchedulePanel';
import InboxPanel from '@/components/admin/InboxPanel';
import DocumentationPanel from '@/components/admin/DocumentationPanel';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status options for bookings and applications
const BOOKING_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'text-yellow-400 bg-yellow-400/10' },
  { value: 'contacted', label: 'Contacted', color: 'text-blue-400 bg-blue-400/10' },
  { value: 'confirmed', label: 'Confirmed', color: 'text-green-400 bg-green-400/10' },
  { value: 'completed', label: 'Completed', color: 'text-emerald-400 bg-emerald-400/10' },
  { value: 'cancelled', label: 'Cancelled', color: 'text-red-400 bg-red-400/10' },
  { value: 'no_show', label: 'No Show', color: 'text-orange-400 bg-orange-400/10' },
];

const APPLICATION_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'text-yellow-400 bg-yellow-400/10' },
  { value: 'contacted', label: 'Contacted', color: 'text-blue-400 bg-blue-400/10' },
  { value: 'interview_scheduled', label: 'Interview', color: 'text-purple-400 bg-purple-400/10' },
  { value: 'approved', label: 'Approved', color: 'text-green-400 bg-green-400/10' },
  { value: 'hired', label: 'Hired', color: 'text-emerald-400 bg-emerald-400/10' },
  { value: 'declined', label: 'Declined', color: 'text-red-400 bg-red-400/10' },
];

export function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [bookings, setBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Edit state for inline updates
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Password change state
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwdConfirm, setNewPwdConfirm] = useState('');
  const [pwdMsg, setPwdMsg] = useState({ type: '', text: '' });
  const [isChangingPwd, setIsChangingPwd] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdMsg({ type: '', text: '' });
    if (!currentPwd || !newPwd) {
      setPwdMsg({ type: 'error', text: 'Please fill all fields' });
      return;
    }
    if (newPwd !== newPwdConfirm) {
      setPwdMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPwd.length < 6) {
      setPwdMsg({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    const token = localStorage.getItem('casa_admin_token');
    if (!token) return;
    setIsChangingPwd(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ current_password: currentPwd, new_password: newPwd }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('casa_admin_token', data.token);
        setCurrentPwd('');
        setNewPwd('');
        setNewPwdConfirm('');
        setPwdMsg({ type: 'success', text: 'Password updated successfully' });
      } else {
        setPwdMsg({ type: 'error', text: data.detail || 'Failed to update password' });
      }
    } catch (_err) {
      setPwdMsg({ type: 'error', text: 'Network error' });
    }
    setIsChangingPwd(false);
  };

  // Fetch data function
  const fetchData = useCallback(async (token) => {
    const authToken = token || localStorage.getItem('casa_admin_token');
    if (!authToken) return;
    
    setIsLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${authToken}` };
      
      const [bookingsRes, applicationsRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/bookings`, { headers }),
        fetch(`${API_URL}/api/applications`, { headers }),
        fetch(`${API_URL}/api/analytics/summary`, { headers })
      ]);
      
      if (bookingsRes.status === 401 || applicationsRes.status === 401) {
        localStorage.removeItem('casa_admin_token');
        setIsAuthenticated(false);
        setBookings([]);
        setApplications([]);
        setAnalytics(null);
        return;
      }
      
      const bookingsData = await bookingsRes.json();
      const applicationsData = await applicationsRes.json();
      const analyticsData = await analyticsRes.json();
      
      // Handle both old array format and new paginated format
      const bookingsList = bookingsData.bookings || (Array.isArray(bookingsData) ? bookingsData : []);
      const applicationsList = applicationsData.applications || (Array.isArray(applicationsData) ? applicationsData : []);
      
      setBookings(bookingsList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setApplications(applicationsList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setIsLoading(false);
  }, []);

  // Check if already authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('casa_admin_token');
      if (token) {
        try {
          const res = await fetch(`${API_URL}/api/admin/verify`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            setIsAuthenticated(true);
            fetchData(token);
          } else {
            localStorage.removeItem('casa_admin_token');
          }
        } catch (err) {
          console.error('Token verification failed:', err);
          localStorage.removeItem('casa_admin_token');
        }
      }
      setIsCheckingAuth(false);
    };
    checkAuth();
  }, [fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginPassword })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        localStorage.setItem('casa_admin_token', data.token);
        setIsAuthenticated(true);
        fetchData(data.token);
      } else {
        setLoginError(data.detail || 'Invalid password');
      }
    } catch (err) {
      setLoginError('Failed to connect to server');
    }
    setIsLoggingIn(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('casa_admin_token');
    setIsAuthenticated(false);
    setBookings([]);
    setApplications([]);
    setAnalytics(null);
  };

  // Update booking status
  const updateBookingStatus = async (bookingId, newStatus) => {
    const token = localStorage.getItem('casa_admin_token');
    if (!token) return;
    
    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        setBookings(prev => prev.map(b => 
          b.id === bookingId ? { ...b, status: newStatus } : b
        ));
      }
    } catch (err) {
      console.error('Failed to update booking:', err);
    }
    setIsSaving(false);
  };

  // Update application status
  const updateApplicationStatus = async (appId, newStatus) => {
    const token = localStorage.getItem('casa_admin_token');
    if (!token) return;
    
    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/applications/${appId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        setApplications(prev => prev.map(a => 
          a.id === appId ? { ...a, status: newStatus } : a
        ));
      }
    } catch (err) {
      console.error('Failed to update application:', err);
    }
    setIsSaving(false);
  };

  // Add admin note
  const addNote = async (id, type) => {
    if (!noteText.trim()) return;
    
    const token = localStorage.getItem('casa_admin_token');
    if (!token) return;
    
    setIsSaving(true);
    try {
      const endpoint = type === 'booking' 
        ? `${API_URL}/api/bookings/${id}`
        : `${API_URL}/api/applications/${id}`;
      
      const res = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ admin_notes: noteText })
      });
      
      if (res.ok) {
        const updated = await res.json();
        if (type === 'booking') {
          setBookings(prev => prev.map(b => b.id === id ? updated : b));
        } else {
          setApplications(prev => prev.map(a => a.id === id ? updated : a));
        }
        setEditingNote(null);
        setNoteText('');
      }
    } catch (err) {
      console.error('Failed to add note:', err);
    }
    setIsSaving(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceName = (code) => {
    const names = { QV: 'Quick Visit', HH: 'Half Hour', HR: 'Full Hour' };
    return names[code] || code;
  };

  const getStatusConfig = (status, type = 'booking') => {
    const statuses = type === 'booking' ? BOOKING_STATUSES : APPLICATION_STATUSES;
    return statuses.find(s => s.value === status) || { value: status, label: status, color: 'text-gray-400 bg-gray-400/10' };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
      case 'approved':
      case 'hired':
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelled':
      case 'declined':
      case 'no_show':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const parseContact = (contact) => {
    if (!contact) return { method: 'unknown', value: '' };
    const colonIndex = contact.indexOf(':');
    if (colonIndex > 0 && colonIndex < 10) {
      const method = contact.substring(0, colonIndex).trim().toLowerCase();
      const value = contact.substring(colonIndex + 1).trim();
      return { method, value };
    }
    if (contact.includes('@')) return { method: 'email', value: contact };
    if (/^\+?[\d\s\-\(\)]+$/.test(contact.replace(/\s/g, ''))) return { method: 'phone', value: contact };
    return { method: 'unknown', value: contact };
  };

  const getApplicationActions = (app) => {
    const { method, value } = parseContact(app.contact);
    const actions = [];
    
    if (method === 'email' || value.includes('@')) {
      actions.push({ type: 'email', label: 'Email', href: `mailto:${value}`, icon: Mail, color: 'bg-[#8B5CFF]/10 text-[#8B5CFF] hover:bg-[#8B5CFF]/20' });
    }
    
    if (method === 'phone' || method === 'call' || method === 'text' || /^\+?[\d\s\-\(\)]+$/.test(value.replace(/\s/g, ''))) {
      const phoneValue = value.replace(/\D/g, '');
      if (phoneValue.length >= 10) {
        actions.push({ type: 'call', label: 'Call', href: `tel:${value}`, icon: Phone, color: 'bg-[#27D7FF]/10 text-[#27D7FF] hover:bg-[#27D7FF]/20' });
        actions.push({ type: 'text', label: 'Text', href: `sms:${value}`, icon: MessageSquare, color: 'bg-[#FF4DA6]/10 text-[#FF4DA6] hover:bg-[#FF4DA6]/20' });
        actions.push({ type: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/${phoneValue}`, icon: MessageSquare, color: 'bg-green-500/10 text-green-400 hover:bg-green-500/20', external: true });
      }
    }
    return actions;
  };

  const filteredBookings = filterStatus === 'all' ? bookings : bookings.filter(b => b.status === filterStatus);
  const filteredApplications = filterStatus === 'all' ? applications : applications.filter(a => a.status === filterStatus);

  // Loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-[#07060B] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#8B5CFF] animate-spin" />
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <>
        <Helmet><title>Admin Login | Casa de Candy</title></Helmet>
        <div className="min-h-screen bg-[#07060B] flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="bg-[#111022] rounded-2xl border border-white/10 p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#8B5CFF]/20 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-[#8B5CFF]" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-[#F4F2FF]">Admin Access</h1>
                <p className="text-sm text-[#A7A3BA] mt-2">Casa de Candy Operations Dashboard</p>
              </div>
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm text-[#A7A3BA] mb-2">Password</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0B0A12] border border-white/10 text-[#F4F2FF] focus:outline-none focus:border-[#8B5CFF]/50"
                    placeholder="Enter admin password"
                    required
                    data-testid="admin-password-input"
                  />
                </div>
                {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF4DA6] to-[#8B5CFF] text-white font-semibold hover:shadow-lg hover:shadow-[#FF4DA6]/30 transition-all disabled:opacity-50"
                  data-testid="admin-login-btn"
                >
                  {isLoggingIn ? 'Logging in...' : 'Login'}
                </button>
              </form>
              
              <div className="mt-6 text-center">
                <Link to="/" className="text-sm text-[#A7A3BA] hover:text-[#8B5CFF] transition-colors">← Back to Home</Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Admin Dashboard | Casa de Candy</title></Helmet>

      <div className="min-h-screen bg-[#07060B]" data-testid="admin-dashboard">
        {/* Header */}
        <header className="bg-[#0B0A12] border-b border-white/10 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link to="/" className="text-[#A7A3BA] hover:text-[#F4F2FF] transition-colors"><Home className="w-5 h-5" /></Link>
                <div>
                  <h1 className="font-heading text-xl font-bold text-[#F4F2FF]">Admin Dashboard</h1>
                  <p className="text-xs text-[#A7A3BA]">Casa de Candy Operations</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => fetchData()} disabled={isLoading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#8B5CFF]/20 text-[#8B5CFF] hover:bg-[#8B5CFF]/30 transition-colors disabled:opacity-50" data-testid="refresh-btn">
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#111022] rounded-xl p-4 border border-[#FF4DA6]/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#FF4DA6]/20 flex items-center justify-center"><Calendar className="w-5 h-5 text-[#FF4DA6]" /></div>
                <div><p className="text-2xl font-bold text-[#F4F2FF]">{bookings.length}</p><p className="text-xs text-[#A7A3BA]">Bookings</p></div>
              </div>
            </div>
            <div className="bg-[#111022] rounded-xl p-4 border border-[#8B5CFF]/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#8B5CFF]/20 flex items-center justify-center"><Users className="w-5 h-5 text-[#8B5CFF]" /></div>
                <div><p className="text-2xl font-bold text-[#F4F2FF]">{applications.length}</p><p className="text-xs text-[#A7A3BA]">Applications</p></div>
              </div>
            </div>
            <div className="bg-[#111022] rounded-xl p-4 border border-[#27D7FF]/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#27D7FF]/20 flex items-center justify-center"><Eye className="w-5 h-5 text-[#27D7FF]" /></div>
                <div><p className="text-2xl font-bold text-[#F4F2FF]">{analytics?.total_events || 0}</p><p className="text-xs text-[#A7A3BA]">Total Events</p></div>
              </div>
            </div>
            <div className="bg-[#111022] rounded-xl p-4 border border-green-500/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center"><Clock className="w-5 h-5 text-green-400" /></div>
                <div><p className="text-2xl font-bold text-[#F4F2FF]">{bookings.filter(b => b.status === 'pending').length}</p><p className="text-xs text-[#A7A3BA]">Pending</p></div>
              </div>
            </div>
          </div>

          {/* Tabs and Filter */}
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <TooltipProvider delayDuration={120} skipDelayDuration={80}>
              <div
                className="flex bg-[#111022] rounded-lg p-1 overflow-x-auto max-w-full no-scrollbar"
                data-testid="admin-tab-strip"
              >
                {[
                  { key: 'bookings', label: `Bookings (${bookings.length})`, tooltip: 'Incoming client bookings — review, contact, confirm.', tone: 'bg-[#FF4DA6] text-white' },
                  { key: 'applications', label: `Vixens (${applications.length})`, tooltip: 'Vixen applications — orientation, screening, hire pipeline.', tone: 'bg-[#8B5CFF] text-white' },
                  { key: 'vixens', label: 'Vixen Profiles', tooltip: 'Active Vixen profiles shown in the public coverflow.', tone: 'bg-[#FF4DA6] text-white' },
                  { key: 'library', label: 'Library', tooltip: 'Photos, video, and background music in one library.', tone: 'bg-[#8B5CFF] text-white' },
                  { key: 'communications', label: 'Communications', tooltip: 'Email, SMS, WhatsApp, and voice channel controls + templates.', tone: 'bg-[#FFB6D8] text-[#07060B]' },
                  { key: 'schedule', label: 'Schedule', tooltip: 'Availability windows, blocks, and buffer rules.', tone: 'bg-[#FF4DA6] text-white' },
                  { key: 'inbox', label: 'Inbox', tooltip: 'Centralised stream of bookings, applications, DMs, and call notes.', tone: 'bg-[#27D7FF] text-[#07060B]' },
                  { key: 'docs', label: 'Docs', tooltip: 'Internal knowledge base — vision, philosophy, onboarding, employee guide.', tone: 'bg-[#D9B46C] text-[#07060B]' },
                  { key: 'settings', label: 'Settings', tooltip: 'Admin password and global configuration.', tone: 'bg-[#27D7FF] text-[#07060B]' },
                ].map((tab) => (
                  <Tooltip key={tab.key}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                          activeTab === tab.key
                            ? tab.tone
                            : 'text-[#A7A3BA] hover:text-[#F4F2FF]'
                        }`}
                        data-testid={`tab-${tab.key}`}
                      >
                        {tab.label}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="center"
                      className="bg-[#0F0E1A] border border-white/10 text-[#F4F2FF] text-xs px-3 py-1.5 max-w-[260px]"
                      data-testid={`tab-tooltip-${tab.key}`}
                    >
                      {tab.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>

            <div className="flex items-center gap-2 ml-auto">
              <Filter className="w-4 h-4 text-[#A7A3BA]" />
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-[#111022] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F2FF] focus:outline-none focus:border-[#8B5CFF]/50" data-testid="status-filter">
                <option value="all">All Status</option>
                {(activeTab === 'bookings' ? BOOKING_STATUSES : APPLICATION_STATUSES).map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20"><RefreshCw className="w-8 h-8 text-[#8B5CFF] animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              {activeTab === 'bookings' && (
                <>
                  {filteredBookings.length === 0 ? (
                    <div className="text-center py-12 bg-[#111022] rounded-xl border border-white/5">
                      <Calendar className="w-12 h-12 text-[#A7A3BA] mx-auto mb-4" />
                      <p className="text-[#A7A3BA]">No bookings found</p>
                    </div>
                  ) : (
                    filteredBookings.map((booking) => {
                      const statusConfig = getStatusConfig(booking.status, 'booking');
                      return (
                        <div key={booking.id} className="bg-[#111022] rounded-xl border border-white/5 overflow-hidden hover:border-[#FF4DA6]/30 transition-colors" data-testid={`booking-${booking.id}`}>
                          <div className="p-4 cursor-pointer" onClick={() => setExpandedItem(expandedItem === booking.id ? null : booking.id)}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
                                  {getStatusIcon(booking.status)}
                                  {statusConfig.label}
                                </div>
                                <div>
                                  <h3 className="font-semibold text-[#F4F2FF]">{booking.name}</h3>
                                  <p className="text-sm text-[#A7A3BA]">{getServiceName(booking.service_code)} · {booking.session_type === 'in-call' ? 'In-Call' : 'Out-Call'}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right hidden sm:block">
                                  <p className="text-sm text-[#F4F2FF]">{booking.preferred_datetime}</p>
                                  <p className="text-xs text-[#A7A3BA]">{formatDate(booking.created_at)}</p>
                                </div>
                                {expandedItem === booking.id ? <ChevronUp className="w-5 h-5 text-[#A7A3BA]" /> : <ChevronDown className="w-5 h-5 text-[#A7A3BA]" />}
                              </div>
                            </div>
                          </div>
                          
                          {expandedItem === booking.id && (
                            <div className="px-4 pb-4 pt-2 border-t border-white/5">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div><p className="text-xs text-[#A7A3BA] mb-1">Contact Method</p><p className="text-sm text-[#F4F2FF] capitalize">{booking.contact_method}</p></div>
                                <div><p className="text-xs text-[#A7A3BA] mb-1">Phone</p><a href={`tel:${booking.phone}`} className="text-sm text-[#27D7FF] hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{booking.phone}</a></div>
                                {booking.notes && <div className="sm:col-span-2"><p className="text-xs text-[#A7A3BA] mb-1">Customer Notes</p><p className="text-sm text-[#F4F2FF]">{booking.notes}</p></div>}
                                
                                {/* Status Update */}
                                <div className="sm:col-span-2 pt-3 border-t border-white/5">
                                  <p className="text-xs text-[#A7A3BA] mb-2">Update Status</p>
                                  <select
                                    value={booking.status}
                                    onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                                    disabled={isSaving}
                                    className="w-full sm:w-auto px-3 py-2 rounded-lg bg-[#0B0A12] border border-white/10 text-[#F4F2FF] text-sm focus:outline-none focus:border-[#FF4DA6]/50"
                                    data-testid={`booking-status-select-${booking.id}`}
                                  >
                                    {BOOKING_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                  </select>
                                </div>
                                
                                {/* Admin Notes */}
                                <div className="sm:col-span-2 pt-3 border-t border-white/5">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs text-[#A7A3BA]">Admin Notes</p>
                                    <button onClick={() => { setEditingNote(`booking-${booking.id}`); setNoteText(''); }} className="text-xs text-[#8B5CFF] hover:underline flex items-center gap-1">
                                      <Edit2 className="w-3 h-3" /> Add Note
                                    </button>
                                  </div>
                                  
                                  {/* Note history */}
                                  {booking.admin_notes_history && booking.admin_notes_history.length > 0 && (
                                    <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                                      {booking.admin_notes_history.map((note, idx) => (
                                        <div key={idx} className="text-xs p-2 rounded bg-[#0B0A12]">
                                          <span className="text-[#A7A3BA]">{formatDate(note.timestamp)}</span>
                                          <p className="text-[#F4F2FF] mt-1">{note.note}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {editingNote === `booking-${booking.id}` && (
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        value={noteText}
                                        onChange={(e) => setNoteText(e.target.value)}
                                        placeholder="Add a note..."
                                        className="flex-1 px-3 py-2 rounded-lg bg-[#0B0A12] border border-white/10 text-[#F4F2FF] text-sm focus:outline-none focus:border-[#8B5CFF]/50"
                                        data-testid={`booking-note-input-${booking.id}`}
                                      />
                                      <button onClick={() => addNote(booking.id, 'booking')} disabled={isSaving || !noteText.trim()} className="px-3 py-2 rounded-lg bg-[#8B5CFF] text-white text-sm disabled:opacity-50">
                                        <Save className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="sm:col-span-2"><p className="text-xs text-[#A7A3BA] mb-1">Booking ID</p><p className="text-xs text-[#8B5CFF] font-mono">{booking.id}</p></div>
                              </div>
                              
                              {/* Quick Actions */}
                              <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                                <a href={`tel:${booking.phone}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#27D7FF]/10 text-[#27D7FF] text-xs hover:bg-[#27D7FF]/20 transition-colors"><Phone className="w-3 h-3" /> Call</a>
                                <a href={`sms:${booking.phone}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FF4DA6]/10 text-[#FF4DA6] text-xs hover:bg-[#FF4DA6]/20 transition-colors"><MessageSquare className="w-3 h-3" /> Text</a>
                                <a href={`https://wa.me/${booking.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 text-xs hover:bg-green-500/20 transition-colors"><MessageSquare className="w-3 h-3" /> WhatsApp</a>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}

              {activeTab === 'applications' && (
                <>
                  {filteredApplications.length === 0 ? (
                    <div className="text-center py-12 bg-[#111022] rounded-xl border border-white/5">
                      <Users className="w-12 h-12 text-[#A7A3BA] mx-auto mb-4" />
                      <p className="text-[#A7A3BA]">No applications found</p>
                    </div>
                  ) : (
                    filteredApplications.map((app) => {
                      const statusConfig = getStatusConfig(app.status, 'application');
                      return (
                        <div key={app.id} className="bg-[#111022] rounded-xl border border-white/5 overflow-hidden hover:border-[#8B5CFF]/30 transition-colors" data-testid={`application-${app.id}`}>
                          <div className="p-4 cursor-pointer" onClick={() => setExpandedItem(expandedItem === app.id ? null : app.id)}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
                                  {getStatusIcon(app.status)}
                                  {statusConfig.label}
                                </div>
                                <div>
                                  <h3 className="font-semibold text-[#F4F2FF]">{app.preferred_name}</h3>
                                  <p className="text-sm text-[#A7A3BA]">{app.city_transport || 'Location not specified'} · {app.availability}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right hidden sm:block">
                                  <p className="text-sm text-[#F4F2FF]">{app.age_confirmed ? '18+' : 'Age not confirmed'}</p>
                                  <p className="text-xs text-[#A7A3BA]">{formatDate(app.created_at)}</p>
                                </div>
                                {expandedItem === app.id ? <ChevronUp className="w-5 h-5 text-[#A7A3BA]" /> : <ChevronDown className="w-5 h-5 text-[#A7A3BA]" />}
                              </div>
                            </div>
                          </div>
                          
                          {expandedItem === app.id && (
                            <div className="px-4 pb-4 pt-2 border-t border-white/5">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div><p className="text-xs text-[#A7A3BA] mb-1">Contact</p><p className="text-sm text-[#27D7FF]">{(() => { const { method, value } = parseContact(app.contact); return `${method !== 'unknown' ? method.charAt(0).toUpperCase() + method.slice(1) + ': ' : ''}${value}`; })()}</p></div>
                                <div><p className="text-xs text-[#A7A3BA] mb-1">Availability</p><p className="text-sm text-[#F4F2FF]">{app.availability}</p></div>
                                {app.experience && <div className="sm:col-span-2"><p className="text-xs text-[#A7A3BA] mb-1">Experience</p><p className="text-sm text-[#F4F2FF]">{app.experience}</p></div>}
                                <div className="sm:col-span-2"><p className="text-xs text-[#A7A3BA] mb-1">Statement</p><p className="text-sm text-[#F4F2FF] bg-[#0B0A12] p-3 rounded-lg">{app.statement}</p></div>
                                {app.photo_urls && app.photo_urls.length > 0 && (
                                  <div className="sm:col-span-2"><p className="text-xs text-[#A7A3BA] mb-2">Photos ({app.photo_urls.length})</p><div className="flex gap-2 flex-wrap">{app.photo_urls.map((url, idx) => (<a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block w-20 h-20 rounded-lg overflow-hidden bg-[#0B0A12] hover:opacity-80 transition-opacity"><img src={url} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" /></a>))}</div></div>
                                )}
                                
                                {/* Status Update */}
                                <div className="sm:col-span-2 pt-3 border-t border-white/5">
                                  <p className="text-xs text-[#A7A3BA] mb-2">Update Status</p>
                                  <select
                                    value={app.status}
                                    onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                                    disabled={isSaving}
                                    className="w-full sm:w-auto px-3 py-2 rounded-lg bg-[#0B0A12] border border-white/10 text-[#F4F2FF] text-sm focus:outline-none focus:border-[#8B5CFF]/50"
                                    data-testid={`app-status-select-${app.id}`}
                                  >
                                    {APPLICATION_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                  </select>
                                </div>
                                
                                {/* Admin Notes */}
                                <div className="sm:col-span-2 pt-3 border-t border-white/5">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs text-[#A7A3BA]">Admin Notes</p>
                                    <button onClick={() => { setEditingNote(`app-${app.id}`); setNoteText(''); }} className="text-xs text-[#8B5CFF] hover:underline flex items-center gap-1"><Edit2 className="w-3 h-3" /> Add Note</button>
                                  </div>
                                  
                                  {app.admin_notes_history && app.admin_notes_history.length > 0 && (
                                    <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                                      {app.admin_notes_history.map((note, idx) => (
                                        <div key={idx} className="text-xs p-2 rounded bg-[#0B0A12]">
                                          <span className="text-[#A7A3BA]">{formatDate(note.timestamp)}</span>
                                          <p className="text-[#F4F2FF] mt-1">{note.note}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {editingNote === `app-${app.id}` && (
                                    <div className="flex gap-2">
                                      <input type="text" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." className="flex-1 px-3 py-2 rounded-lg bg-[#0B0A12] border border-white/10 text-[#F4F2FF] text-sm focus:outline-none focus:border-[#8B5CFF]/50" data-testid={`app-note-input-${app.id}`} />
                                      <button onClick={() => addNote(app.id, 'application')} disabled={isSaving || !noteText.trim()} className="px-3 py-2 rounded-lg bg-[#8B5CFF] text-white text-sm disabled:opacity-50"><Save className="w-4 h-4" /></button>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="sm:col-span-2"><p className="text-xs text-[#A7A3BA] mb-1">Application ID</p><p className="text-xs text-[#8B5CFF] font-mono">{app.id}</p></div>
                              </div>
                              
                              {/* Quick Actions */}
                              <div className="flex gap-2 mt-4 pt-4 border-t border-white/5 flex-wrap">
                                {getApplicationActions(app).map((action, idx) => (
                                  <a key={idx} href={action.href} target={action.external ? "_blank" : undefined} rel={action.external ? "noopener noreferrer" : undefined} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-colors ${action.color}`}>
                                    <action.icon className="w-3 h-3" /> {action.label}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}

              {activeTab === 'vixens' && <VixenManager />}
              {activeTab === 'library' && <LibraryPanel />}
              {activeTab === 'communications' && <CommunicationsPanel />}
              {activeTab === 'schedule' && <SchedulePanel />}
              {activeTab === 'inbox' && <InboxPanel />}
              {activeTab === 'docs' && <DocumentationPanel />}

              {activeTab === 'settings' && (
                <div className="max-w-xl bg-[#111022] border border-white/5 rounded-xl p-6" data-testid="admin-settings-panel">
                  <h2 className="text-lg font-semibold text-[#F4F2FF] mb-2">Change Admin Password</h2>
                  <p className="text-sm text-[#A7A3BA] mb-5">
                    Update the passcode used to access this dashboard. You stay logged in after changing.
                  </p>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block text-xs text-[#A7A3BA] mb-1">Current password</label>
                      <input
                        type="password"
                        value={currentPwd}
                        onChange={(e) => setCurrentPwd(e.target.value)}
                        className="w-full px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#27D7FF]/60"
                        data-testid="settings-current-password"
                        autoComplete="current-password"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A7A3BA] mb-1">New password</label>
                      <input
                        type="password"
                        value={newPwd}
                        onChange={(e) => setNewPwd(e.target.value)}
                        className="w-full px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#27D7FF]/60"
                        data-testid="settings-new-password"
                        autoComplete="new-password"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A7A3BA] mb-1">Confirm new password</label>
                      <input
                        type="password"
                        value={newPwdConfirm}
                        onChange={(e) => setNewPwdConfirm(e.target.value)}
                        className="w-full px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#27D7FF]/60"
                        data-testid="settings-confirm-password"
                        autoComplete="new-password"
                      />
                    </div>
                    {pwdMsg.text && (
                      <div
                        className={`text-sm px-3 py-2 rounded-lg ${
                          pwdMsg.type === 'success'
                            ? 'text-green-300 bg-green-500/10 border border-green-500/20'
                            : 'text-red-300 bg-red-500/10 border border-red-500/20'
                        }`}
                        data-testid="settings-pwd-message"
                      >
                        {pwdMsg.text}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={isChangingPwd}
                      className="px-4 py-2 bg-[#27D7FF] text-[#07060B] rounded-lg text-sm font-medium hover:bg-[#27D7FF]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid="settings-save-password"
                    >
                      {isChangingPwd ? 'Saving…' : 'Update password'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </>
  );
}

export default AdminDashboard;
